// What the server's refusals say, in the reader's language.
//
// `err_<code>` for every code the API answers with today (grep for httpx.* in
// internal/), plus the codes the client makes up itself (`network`, `aborted`,
// `bad_response`, `unknown`). `field_*` turns a 422's per-field complaint into
// a sentence: a label for each field name the API uses, and a handful of
// shapes the server's English falls into. `messageOf` in lib/ui.tsx is what
// reads this; a screen that adds a new code puts `err_<code>` in its own
// namespace and passes it along.

import { defineMessages } from './index'

export default defineMessages(
  {
    // ------------------------------------------------------------ generic --
    err_unauthorized: 'Tizimga qaytadan kiring: seans tugagan.',
    err_forbidden: 'Bu amal uchun ruxsatingiz yo‘q.',
    err_not_found: 'Topilmadi: u o‘chirilgan yoki manzil noto‘g‘ri.',
    err_rate_limited: 'So‘rovlar juda ko‘p. Birozdan keyin qayta urinib ko‘ring.',
    err_invalid: 'Ba’zi maydonlar noto‘g‘ri to‘ldirilgan.',
    err_bad_json: 'So‘rov noto‘g‘ri shaklda yuborildi. Sahifani yangilab, qayta urinib ko‘ring.',
    err_bad_id: 'Havola noto‘g‘ri: identifikator yaroqsiz.',
    err_internal: 'Serverda xatolik yuz berdi. Birozdan keyin qayta urinib ko‘ring.',
    err_method_not_allowed: 'Bu amalni bajarib bo‘lmaydi.',
    err_conflict: 'Bu amal hozirgi holat bilan to‘qnashdi. Sahifani yangilab, qayta urinib ko‘ring.',
    err_unavailable: 'Server vaqtincha javob bermayapti. Birozdan keyin qayta urinib ko‘ring.',
    // The server refuses these to keep other sites out (a body that is not
    // JSON, a change sent from another site). This app never sends either,
    // so seeing one means the page is stale or was opened at another address.
    err_unsupported_media_type: 'Server so‘rovni yuborilgan ko‘rinishida qabul qilmadi. Sahifani yangilab, qayta urinib ko‘ring.',
    err_cross_origin:
      'So‘rov rad etildi, chunki u shu saytdan yuborilmagan. Saytni o‘z manzilida oching, sahifani yangilab, qayta urinib ko‘ring.',

    // ------------------------------------------------------- client-side --
    err_network: 'Aloqada xatolik. Internetni tekshirib, qayta urinib ko‘ring.',
    err_aborted: 'Yuklash bekor qilindi.',
    err_bad_response: 'Server kutilmagan javob qaytardi. Birozdan keyin qayta urinib ko‘ring.',
    err_unknown: 'Kutilmagan xatolik yuz berdi. Qayta urinib ko‘ring.',

    // ------------------------------------------------------------ sign-in --
    err_bad_credentials: 'Login yoki parol noto‘g‘ri.',
    err_wrong_current_password: 'Joriy parol noto‘g‘ri.',
    err_locked: 'Juda ko‘p noto‘g‘ri urinish. Hisob vaqtincha bloklandi, birozdan keyin qayta urinib ko‘ring.',
    err_locked_until: 'Juda ko‘p noto‘g‘ri urinish. Hisob {time} gacha bloklandi.',
    err_password_change_required: 'Davom etishdan oldin o‘z parolingizni tanlang.',
    // A sign-in on another device ended this session: the platform keeps a
    // learner to one device at a time.
    err_session_replaced: 'Hisobingizga boshqa qurilmadan kirildi, shuning uchun bu yerdagi seans yakunlandi. Qaytadan kiring.',
    err_taken: 'Bu qiymat allaqachon band.',
    err_taken_phone: 'Bu telefon raqami bilan hisob allaqachon mavjud.',
    err_taken_username: 'Bu login band. Boshqasini tanlang.',
    err_phone_taken:
      'Bu raqam bilan hisob allaqachon bor. Kirish sahifasida telefon raqamingiz bilan kiring yoki administratoringizga murojaat qiling.',
    err_invalid_locale: 'Bu til qo‘llab-quvvatlanmaydi.',

    // -------------------------------------------------------------- files --
    err_too_large: 'Fayl juda katta.',
    err_too_large_limit: 'Fayl juda katta: ko‘pi bilan {limit} bo‘lishi mumkin.',
    err_unsupported_type: 'Bu turdagi faylni bu yerga yuklab bo‘lmaydi.',
    err_bad_multipart: 'Faylni o‘qib bo‘lmadi. Qayta yuklab ko‘ring.',
    err_not_multipart: 'Fayl noto‘g‘ri yuborildi. Qayta yuklab ko‘ring.',
    err_file_in_use: 'Bu fayl hali darslarda yoki topshiriqlarda ishlatilmoqda. Avval u yerdan olib tashlang.',

    // ------------------------------------------------------------ editing --
    err_empty_patch: 'Hech narsa o‘zgartirilmadi.',
    err_no_changes: 'Hech narsa o‘zgartirilmadi.',
    err_nothing_to_update: 'Hech narsa o‘zgartirilmadi.',
    err_stale_order: 'Tartib eskirgan: uni boshqa birov o‘zgartirgan. Sahifani yangilab, qayta urinib ko‘ring.',
    err_no_platform: 'Platforma aniqlanmadi. Manzilni tekshiring.',
    err_last_owner: 'Platformada kamida bitta egasi qolishi kerak. Avval boshqa xodimni ega qiling.',
    err_stream_in_use: 'Bu guruhda hali talabalar bor. Avval ularni boshqa guruhga o‘tkazing.',
    err_group_in_use: 'Bu kichik guruhda hali talabalar bor. Avval ularni boshqa kichik guruhga o‘tkazing.',

    // --------------------------------------------------------- homework --
    err_no_homework: 'Bu darsda uy vazifasi yo‘q.',
    err_homework_has_submissions: 'Bu uy vazifasiga javoblar topshirilgan, shuning uchun uni olib tashlab bo‘lmaydi.',
    err_homework_required: 'Dars shartlari hali uy vazifasini talab qiladi. Avval bu shartni o‘chiring.',
    err_submission_pending: 'Oldingi javobingiz hali tekshirilmoqda. Natijani kuting.',

    // ---------------------------------------------------------- lessons --
    err_blocked: 'Bu dars hali siz uchun ochilmagan.',
    err_blocked_progress: 'Avval oldingi darslarni tugating.',
    err_blocked_homework: 'Avval oldingi uy vazifasini topshiring va qabul qilinishini kuting.',
    err_blocked_schedule: 'Bu bo‘lim keyinroq ochiladi.',
    err_blocked_access: 'Bu bo‘lim siz uchun yopiq.',
    err_blocked_unpublished: 'Bu dars hali chop etilmagan.',
    err_bad_span: 'Video ko‘rilgani haqidagi ma’lumot noto‘g‘ri. Sahifani yangilang.',
    err_not_gateable: 'Bu blokda ko‘rish jarayoni hisoblanmaydi.',

    // ----------------------------------------------------------- videos --
    err_video_busy: 'Bu video boshqa joyda yuklanmoqda yoki qayta ishlanmoqda. Tugashini kuting.',
    err_video_has_media: 'Bu videoning fayli allaqachon bor. Boshqa yozuv uchun yangi video qo‘shing.',
    err_video_has_no_media: 'Videoni tayyor deb belgilab bo‘lmaydi: uning fayli serverda yo‘q.',
    err_video_in_use: 'Bu video hali darslarda ishlatilmoqda. Avval u yerdan olib tashlang.',
    err_video_media_missing: 'Video fayli serverda topilmadi. Administratorga murojaat qiling.',
    err_video_misconfigured: 'Video noto‘g‘ri sozlangan: tashqi xizmatdagi identifikatori ko‘rsatilmagan.',
    err_video_not_local: 'Bu video tashqi xizmatda saqlanadi, uning faylini o‘sha yerga yuklang.',
    err_video_not_ready: 'Video hali tayyor emas.',
    err_video_transcoding: 'Video hali qayta ishlanmoqda. Tugashini kuting.',
    err_ffprobe_missing: 'Serverda videoni tekshirish vositasi (ffprobe) o‘rnatilmagan. Administratorga murojaat qiling.',

    // -------------------------------------------------- reports, points --
    err_bad_filter: 'Filtr qiymati noto‘g‘ri.',
    err_bad_action: 'Filtrdagi amal turi noto‘g‘ri.',
    err_bad_actor_id: 'Filtrdagi xodim noto‘g‘ri tanlangan.',
    err_bad_from: 'Boshlanish sanasi noto‘g‘ri.',
    err_bad_to: 'Tugash sanasi noto‘g‘ri.',
    err_bad_range: 'Boshlanish sanasi tugash sanasidan keyin bo‘lmasligi kerak.',
    err_bad_grouping: 'Modullar yoki darslar bo‘yicha guruhlashni tanlang.',
    err_bad_report: 'Bunday hisobot yo‘q.',
    err_course_required: 'Avval kursni tanlang.',
    err_missing_course: 'Avval kursni tanlang.',
    err_bad_amount: 'Ballar soni noto‘g‘ri.',
    err_bad_award: 'Ball berish uchun talaba va kursni tanlang.',
    err_bad_reason: 'Ball berish sababi noto‘g‘ri.',
    err_gamification_disabled: 'Bu kursda ballar tizimi o‘chirilgan.',

    // ----------------------------------------------------- field shapes --
    field_required: '«{field}» maydonini to‘ldiring.',
    field_too_long: '«{field}» juda uzun: ko‘pi bilan {max} ta belgi.',
    field_length: '«{field}» {min} tadan {max} tagacha belgidan iborat bo‘lishi kerak.',
    field_range: '«{field}» {min} dan {max} gacha bo‘lishi kerak.',
    field_bad_phone: 'Telefon raqami noto‘g‘ri. Uni davlat kodi bilan yozing, masalan +998 90 123 45 67.',
    field_color: 'Rang #f60 yoki #ff6600 ko‘rinishida yoziladi.',
    field_bad_url: 'Havola noto‘g‘ri: https:// bilan boshlanadigan to‘liq manzilni kiriting.',
    field_not_enrolled: 'Bu talaba ushbu kursga yozilmagan.',
    field_same_password: 'Bu sizning hozirgi parolingiz. Boshqasini tanlang.',
    field_username_shape:
      'Login {min}–{max} ta belgidan iborat bo‘lsin: harflar, raqamlar, nuqta, chiziqcha yoki pastki chiziq.',
    field_invalid: '«{field}» maydoni noto‘g‘ri to‘ldirilgan.',

    // ----------------------------------------------------- field labels --
    field_title: 'Nomi',
    field_name: 'Nomi',
    field_description: 'Tavsif',
    field_full_name: 'Ism-familiya',
    field_phone: 'Telefon raqami',
    field_phones: 'Telefon raqamlari',
    field_username: 'Login',
    field_login: 'Login',
    field_password: 'Parol',
    field_current_password: 'Joriy parol',
    field_new_password: 'Yangi parol',
    field_email: 'Elektron pochta',
    field_locale: 'Til',
    field_kind: 'Turi',
    field_status: 'Holati',
    field_grade: 'Baho',
    field_body: 'Javob matni',
    field_instructions: 'Topshiriq matni',
    field_questions: 'Savollar',
    field_pass_pct: 'O‘tish bali (%)',
    field_ids: 'Tartib',
    field_access: 'Kirish huquqi',
    field_access_start_at: 'Ochilish sanasi',
    field_file: 'Fayl',
    field_files: 'Fayllar',
    field_section_id: 'Bo‘lim',
    field_course_id: 'Kurs',
    field_user_id: 'Talaba',
    field_students: 'Talabalar',
    field_stream_id: 'Guruh',
    field_group_id: 'Kichik guruh',
    field_mentor_id: 'Mentor',
    field_note: 'Izoh',
    field_comment: 'Izoh',
    field_review_comment: 'Izoh',
    field_role: 'Rol',
    field_reason: 'Sabab',
    field_reason_text: 'Sabab',
    field_amount: 'Ballar soni',
    field_points: 'Ballar',
    field_min_video_watch_percent: 'Videoni ko‘rish chegarasi (%)',
    field_provider: 'Video xizmati',
    field_duration_seconds: 'Davomiyligi',
    field_source_path: 'Fayl manzili',
    field_external_id: 'Tashqi identifikator',
    field_payload: 'Blok mazmuni',
    field_video_id: 'Video',
    field_file_id: 'Fayl',
    field_url: 'Havola',
    field_height: 'Balandligi',
    field_html: 'Matn',
    field_colors: 'Rang',
    field_logo_path: 'Logotip',
    field_favicon_path: 'Sayt belgisi',
    field_subdomain: 'Subdomen',
    field_cover_file_id: 'Muqova rasmi',
    field_image_file_id: 'Bo‘lim rasmi',
  },
  {
    en: {
      err_unauthorized: 'Please sign in again: your session has ended.',
      err_forbidden: 'You don’t have permission to do this.',
      err_not_found: 'Not found: it may have been deleted, or the address is wrong.',
      err_rate_limited: 'Too many requests. Please try again a little later.',
      err_invalid: 'Some fields are not filled in correctly.',
      err_bad_json: 'The request was malformed. Reload the page and try again.',
      err_bad_id: 'The link is broken: the ID is not valid.',
      err_internal: 'Something went wrong on the server. Please try again shortly.',
      err_method_not_allowed: 'This action is not supported.',
      err_conflict: 'This conflicts with the current state. Reload the page and try again.',
      err_unavailable: 'The server is temporarily unavailable. Please try again shortly.',
      err_unsupported_media_type: 'The server didn’t accept the request in the form it was sent. Reload the page and try again.',
      err_cross_origin:
        'The request was refused because it didn’t come from this site. Open the site at its own address, reload the page and try again.',

      err_network: 'Connection problem. Check your internet connection and try again.',
      err_aborted: 'The upload was cancelled.',
      err_bad_response: 'The server sent an unexpected response. Please try again shortly.',
      err_unknown: 'Something unexpected happened. Please try again.',

      err_bad_credentials: 'Incorrect username or password.',
      err_wrong_current_password: 'The current password is incorrect.',
      err_locked: 'Too many failed attempts. The account is temporarily locked; please try again later.',
      err_locked_until: 'Too many failed attempts. The account is locked until {time}.',
      err_password_change_required: 'Choose your own password before you continue.',
      err_session_replaced: 'Your account was signed in on another device, so this session has ended. Please sign in again.',
      err_taken: 'This is already taken.',
      err_taken_phone: 'An account with this phone number already exists.',
      err_taken_username: 'This username is taken. Please choose another one.',
      err_phone_taken:
        'This number already has an account. Sign in with your phone number on the sign-in page, or ask your administrator.',
      err_invalid_locale: 'This language is not supported.',

      err_too_large: 'The file is too large.',
      err_too_large_limit: 'The file is too large: the limit is {limit}.',
      err_unsupported_type: 'This type of file can’t be uploaded here.',
      err_bad_multipart: 'The file could not be read. Try uploading it again.',
      err_not_multipart: 'The file was not sent correctly. Try uploading it again.',
      err_file_in_use: 'This file is still used in lessons or submissions. Remove it from there first.',

      err_empty_patch: 'Nothing was changed.',
      err_no_changes: 'Nothing was changed.',
      err_nothing_to_update: 'Nothing was changed.',
      err_stale_order: 'The order is out of date — someone else changed it. Reload the page and try again.',
      err_no_platform: 'The platform could not be identified. Check the address.',
      err_last_owner: 'The platform must keep at least one owner. Make someone else an owner first.',
      err_stream_in_use: 'This stream still has students. Move them to another stream first.',
      err_group_in_use: 'This group still has students. Move them to another group first.',

      err_no_homework: 'This lesson has no homework.',
      err_homework_has_submissions: 'Students have already handed in work for this homework, so it can’t be removed.',
      err_homework_required: 'The lesson’s completion rules still require homework. Turn that requirement off first.',
      err_submission_pending: 'Your last submission is still waiting to be reviewed.',

      err_blocked: 'This lesson isn’t open to you yet.',
      err_blocked_progress: 'Finish the previous lessons first.',
      err_blocked_homework: 'Hand in the previous homework first and wait for it to be accepted.',
      err_blocked_schedule: 'This module opens later.',
      err_blocked_access: 'This module is closed to you.',
      err_blocked_unpublished: 'This lesson hasn’t been published yet.',
      err_bad_span: 'The viewing progress report was not valid. Please reload the page.',
      err_not_gateable: 'This block doesn’t track viewing progress.',

      err_video_busy: 'This video is being uploaded or converted elsewhere. Wait for it to finish.',
      err_video_has_media: 'This video already has a recording. Add a new video for a different one.',
      err_video_has_no_media: 'The video can’t be marked ready: its file is not on the server.',
      err_video_in_use: 'This video is still used in lessons. Remove it from them first.',
      err_video_media_missing: 'The video file is missing from the server. Contact your administrator.',
      err_video_misconfigured: 'The video is set up incorrectly: its ID at the hosting service is missing.',
      err_video_not_local: 'This video is hosted by an external service; upload its file there.',
      err_video_not_ready: 'The video isn’t ready yet.',
      err_video_transcoding: 'The video is still being converted. Wait for it to finish.',
      err_ffprobe_missing: 'The server can’t inspect video because ffprobe is not installed. Contact your administrator.',

      err_bad_filter: 'A filter value is not valid.',
      err_bad_action: 'The action in the filter is not valid.',
      err_bad_actor_id: 'The staff member in the filter is not valid.',
      err_bad_from: 'The start date is not valid.',
      err_bad_to: 'The end date is not valid.',
      err_bad_range: 'The start date must not be after the end date.',
      err_bad_grouping: 'Choose grouping by modules or by lessons.',
      err_bad_report: 'There is no such report.',
      err_course_required: 'Choose a course first.',
      err_missing_course: 'Choose a course first.',
      err_bad_amount: 'The number of points is not valid.',
      err_bad_award: 'Choose a student and a course to award points.',
      err_bad_reason: 'The reason for the points is not valid.',
      err_gamification_disabled: 'Points are turned off for this course.',

      field_required: 'Please fill in “{field}”.',
      field_too_long: '“{field}” is too long: at most {max} characters.',
      field_length: '“{field}” must be {min} to {max} characters long.',
      field_range: '“{field}” must be between {min} and {max}.',
      field_bad_phone: 'The phone number is not valid. Write it with the country code, e.g. +998 90 123 45 67.',
      field_color: 'Write the colour as #f60 or #ff6600.',
      field_bad_url: 'The link is not valid: enter a full address starting with https://.',
      field_not_enrolled: 'This student is not enrolled on this course.',
      field_same_password: 'This is already your password. Please choose a different one.',
      field_username_shape: 'A username has {min}–{max} characters: letters, digits, dot, hyphen or underscore.',
      field_invalid: '{field}: {detail}',

      field_title: 'Title',
      field_name: 'Name',
      field_description: 'Description',
      field_full_name: 'Full name',
      field_phone: 'Phone number',
      field_phones: 'Phone numbers',
      field_username: 'Username',
      field_login: 'Username or phone',
      field_password: 'Password',
      field_current_password: 'Current password',
      field_new_password: 'New password',
      field_email: 'Email',
      field_locale: 'Language',
      field_kind: 'Type',
      field_status: 'Status',
      field_grade: 'Grade',
      field_body: 'Answer',
      field_instructions: 'Instructions',
      field_questions: 'Questions',
      field_pass_pct: 'Pass mark (%)',
      field_ids: 'Order',
      field_access: 'Access',
      field_access_start_at: 'Opening date',
      field_file: 'File',
      field_files: 'Files',
      field_section_id: 'Module',
      field_course_id: 'Course',
      field_user_id: 'Student',
      field_students: 'Students',
      field_stream_id: 'Stream',
      field_group_id: 'Group',
      field_mentor_id: 'Mentor',
      field_note: 'Note',
      field_comment: 'Comment',
      field_review_comment: 'Comment',
      field_role: 'Role',
      field_reason: 'Reason',
      field_reason_text: 'Reason',
      field_amount: 'Number of points',
      field_points: 'Points',
      field_min_video_watch_percent: 'Required viewing (%)',
      field_provider: 'Video service',
      field_duration_seconds: 'Duration',
      field_source_path: 'File path',
      field_external_id: 'External ID',
      field_payload: 'Block content',
      field_video_id: 'Video',
      field_file_id: 'File',
      field_url: 'Link',
      field_height: 'Height',
      field_html: 'Text',
      field_colors: 'Colour',
      field_logo_path: 'Logo',
      field_favicon_path: 'Favicon',
      field_subdomain: 'Subdomain',
      field_cover_file_id: 'Cover image',
      field_image_file_id: 'Module image',
    },
    ru: {
      err_unauthorized: 'Войдите снова: сеанс завершён.',
      err_forbidden: 'У вас нет прав на это действие.',
      err_not_found: 'Не найдено: возможно, объект удалён или адрес указан неверно.',
      err_rate_limited: 'Слишком много запросов. Повторите попытку чуть позже.',
      err_invalid: 'Некоторые поля заполнены неверно.',
      err_bad_json: 'Запрос сформирован неверно. Обновите страницу и повторите попытку.',
      err_bad_id: 'Неверная ссылка: недопустимый идентификатор.',
      err_internal: 'На сервере произошла ошибка. Повторите попытку чуть позже.',
      err_method_not_allowed: 'Это действие не поддерживается.',
      err_conflict: 'Действие противоречит текущему состоянию. Обновите страницу и повторите попытку.',
      err_unavailable: 'Сервер временно недоступен. Повторите попытку чуть позже.',
      err_unsupported_media_type: 'Сервер не принял запрос в том виде, в котором он был отправлен. Обновите страницу и повторите попытку.',
      err_cross_origin:
        'Запрос отклонён: он отправлен не с этого сайта. Откройте сайт по его собственному адресу, обновите страницу и повторите попытку.',

      err_network: 'Проблема со связью. Проверьте подключение к интернету и повторите попытку.',
      err_aborted: 'Загрузка отменена.',
      err_bad_response: 'Сервер вернул неожиданный ответ. Повторите попытку чуть позже.',
      err_unknown: 'Произошла непредвиденная ошибка. Повторите попытку.',

      err_bad_credentials: 'Неверный логин или пароль.',
      err_wrong_current_password: 'Текущий пароль указан неверно.',
      err_locked: 'Слишком много неудачных попыток. Аккаунт временно заблокирован, повторите попытку позже.',
      err_locked_until: 'Слишком много неудачных попыток. Аккаунт заблокирован до {time}.',
      err_password_change_required: 'Прежде чем продолжить, придумайте свой пароль.',
      err_session_replaced: 'В ваш аккаунт вошли с другого устройства, поэтому этот сеанс завершён. Войдите снова.',
      err_taken: 'Это значение уже занято.',
      err_taken_phone: 'Аккаунт с этим номером телефона уже существует.',
      err_taken_username: 'Этот логин уже занят. Выберите другой.',
      err_phone_taken:
        'С этим номером уже есть аккаунт. Войдите по номеру телефона на странице входа или обратитесь к администратору.',
      err_invalid_locale: 'Этот язык не поддерживается.',

      err_too_large: 'Файл слишком большой.',
      err_too_large_limit: 'Файл слишком большой: максимум {limit}.',
      err_unsupported_type: 'Файлы этого типа сюда загружать нельзя.',
      err_bad_multipart: 'Не удалось прочитать файл. Попробуйте загрузить его ещё раз.',
      err_not_multipart: 'Файл отправлен неверно. Попробуйте загрузить его ещё раз.',
      err_file_in_use: 'Этот файл ещё используется в уроках или работах. Сначала уберите его оттуда.',

      err_empty_patch: 'Ничего не изменено.',
      err_no_changes: 'Ничего не изменено.',
      err_nothing_to_update: 'Ничего не изменено.',
      err_stale_order: 'Порядок устарел — его изменил кто-то другой. Обновите страницу и повторите попытку.',
      err_no_platform: 'Не удалось определить платформу. Проверьте адрес.',
      err_last_owner: 'У платформы должен остаться хотя бы один владелец. Сначала назначьте владельцем другого сотрудника.',
      err_stream_in_use: 'В этом потоке ещё есть студенты. Сначала переведите их в другой поток.',
      err_group_in_use: 'В этой группе ещё есть студенты. Сначала переведите их в другую группу.',

      err_no_homework: 'В этом уроке нет домашнего задания.',
      err_homework_has_submissions: 'По этому заданию уже сданы работы, поэтому его нельзя удалить.',
      err_homework_required: 'Условия прохождения урока всё ещё требуют домашнее задание. Сначала отключите это условие.',
      err_submission_pending: 'Ваша предыдущая работа ещё ждёт проверки.',

      err_blocked: 'Этот урок вам пока недоступен.',
      err_blocked_progress: 'Сначала пройдите предыдущие уроки.',
      err_blocked_homework: 'Сначала сдайте предыдущее домашнее задание и дождитесь, пока его примут.',
      err_blocked_schedule: 'Этот модуль откроется позже.',
      err_blocked_access: 'Этот модуль для вас закрыт.',
      err_blocked_unpublished: 'Этот урок ещё не опубликован.',
      err_bad_span: 'Некорректные данные о просмотре. Обновите страницу.',
      err_not_gateable: 'В этом блоке прогресс просмотра не отслеживается.',

      err_video_busy: 'Это видео сейчас загружается или обрабатывается в другом месте. Дождитесь завершения.',
      err_video_has_media: 'У этого видео уже есть запись. Для другой записи добавьте новое видео.',
      err_video_has_no_media: 'Видео нельзя отметить как готовое: его файла нет на сервере.',
      err_video_in_use: 'Это видео ещё используется в уроках. Сначала уберите его оттуда.',
      err_video_media_missing: 'Файл видео отсутствует на сервере. Обратитесь к администратору.',
      err_video_misconfigured: 'Видео настроено неверно: не указан его идентификатор во внешнем сервисе.',
      err_video_not_local: 'Это видео размещено во внешнем сервисе — загрузите файл туда.',
      err_video_not_ready: 'Видео ещё не готово.',
      err_video_transcoding: 'Видео ещё обрабатывается. Дождитесь завершения.',
      err_ffprobe_missing: 'Сервер не может проверить видео: не установлен ffprobe. Обратитесь к администратору.',

      err_bad_filter: 'Недопустимое значение фильтра.',
      err_bad_action: 'В фильтре указано недопустимое действие.',
      err_bad_actor_id: 'В фильтре выбран недопустимый сотрудник.',
      err_bad_from: 'Неверная дата начала.',
      err_bad_to: 'Неверная дата окончания.',
      err_bad_range: 'Дата начала не может быть позже даты окончания.',
      err_bad_grouping: 'Выберите группировку по модулям или по урокам.',
      err_bad_report: 'Такого отчёта нет.',
      err_course_required: 'Сначала выберите курс.',
      err_missing_course: 'Сначала выберите курс.',
      err_bad_amount: 'Неверное количество баллов.',
      err_bad_award: 'Чтобы начислить баллы, выберите студента и курс.',
      err_bad_reason: 'Недопустимая причина начисления баллов.',
      err_gamification_disabled: 'В этом курсе баллы отключены.',

      field_required: 'Заполните поле «{field}».',
      field_too_long: 'Поле «{field}» слишком длинное: не более {max} символов.',
      field_length: 'Поле «{field}» должно содержать от {min} до {max} символов.',
      field_range: 'Значение поля «{field}» должно быть от {min} до {max}.',
      field_bad_phone: 'Неверный номер телефона. Укажите его с кодом страны, например +998 90 123 45 67.',
      field_color: 'Укажите цвет в виде #f60 или #ff6600.',
      field_bad_url: 'Неверная ссылка: укажите полный адрес, начинающийся с https://.',
      field_not_enrolled: 'Этот студент не записан на этот курс.',
      field_same_password: 'Это ваш текущий пароль. Выберите другой.',
      field_username_shape: 'Логин — от {min} до {max} символов: буквы, цифры, точка, дефис или подчёркивание.',
      field_invalid: 'Поле «{field}» заполнено неверно.',

      field_title: 'Название',
      field_name: 'Название',
      field_description: 'Описание',
      field_full_name: 'Имя и фамилия',
      field_phone: 'Номер телефона',
      field_phones: 'Номера телефонов',
      field_username: 'Логин',
      field_login: 'Логин или телефон',
      field_password: 'Пароль',
      field_current_password: 'Текущий пароль',
      field_new_password: 'Новый пароль',
      field_email: 'Электронная почта',
      field_locale: 'Язык',
      field_kind: 'Тип',
      field_status: 'Статус',
      field_grade: 'Оценка',
      field_body: 'Текст ответа',
      field_instructions: 'Условие задания',
      field_questions: 'Вопросы',
      field_pass_pct: 'Проходной балл (%)',
      field_ids: 'Порядок',
      field_access: 'Доступ',
      field_access_start_at: 'Дата открытия',
      field_file: 'Файл',
      field_files: 'Файлы',
      field_section_id: 'Модуль',
      field_course_id: 'Курс',
      field_user_id: 'Студент',
      field_students: 'Студенты',
      field_stream_id: 'Поток',
      field_group_id: 'Группа',
      field_mentor_id: 'Ментор',
      field_note: 'Примечание',
      field_comment: 'Комментарий',
      field_review_comment: 'Комментарий',
      field_role: 'Роль',
      field_reason: 'Причина',
      field_reason_text: 'Причина',
      field_amount: 'Количество баллов',
      field_points: 'Баллы',
      field_min_video_watch_percent: 'Порог просмотра видео (%)',
      field_provider: 'Видеосервис',
      field_duration_seconds: 'Длительность',
      field_source_path: 'Путь к файлу',
      field_external_id: 'Внешний идентификатор',
      field_payload: 'Содержимое блока',
      field_video_id: 'Видео',
      field_file_id: 'Файл',
      field_url: 'Ссылка',
      field_height: 'Высота',
      field_html: 'Текст',
      field_colors: 'Цвет',
      field_logo_path: 'Логотип',
      field_favicon_path: 'Значок сайта',
      field_subdomain: 'Поддомен',
      field_cover_file_id: 'Обложка',
      field_image_file_id: 'Изображение модуля',
    },
    de: {
      err_unauthorized: 'Bitte melden Sie sich erneut an: Ihre Sitzung ist abgelaufen.',
      err_forbidden: 'Dafür fehlt Ihnen die Berechtigung.',
      err_not_found: 'Nicht gefunden: Möglicherweise wurde es gelöscht oder die Adresse ist falsch.',
      err_rate_limited: 'Zu viele Anfragen. Bitte versuchen Sie es etwas später erneut.',
      err_invalid: 'Einige Felder sind nicht korrekt ausgefüllt.',
      err_bad_json: 'Die Anfrage war fehlerhaft. Laden Sie die Seite neu und versuchen Sie es erneut.',
      err_bad_id: 'Der Link ist fehlerhaft: Die ID ist ungültig.',
      err_internal: 'Auf dem Server ist ein Fehler aufgetreten. Bitte versuchen Sie es gleich noch einmal.',
      err_method_not_allowed: 'Diese Aktion wird nicht unterstützt.',
      err_conflict: 'Das widerspricht dem aktuellen Stand. Laden Sie die Seite neu und versuchen Sie es erneut.',
      err_unavailable: 'Der Server ist vorübergehend nicht erreichbar. Bitte versuchen Sie es gleich noch einmal.',
      err_unsupported_media_type: 'Der Server hat die Anfrage in dieser Form nicht angenommen. Laden Sie die Seite neu und versuchen Sie es erneut.',
      err_cross_origin:
        'Die Anfrage wurde abgelehnt, weil sie nicht von dieser Website kam. Öffnen Sie die Website unter ihrer eigenen Adresse, laden Sie die Seite neu und versuchen Sie es erneut.',

      err_network: 'Verbindungsproblem. Prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
      err_aborted: 'Der Upload wurde abgebrochen.',
      err_bad_response: 'Der Server hat unerwartet geantwortet. Bitte versuchen Sie es gleich noch einmal.',
      err_unknown: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',

      err_bad_credentials: 'Benutzername oder Passwort ist falsch.',
      err_wrong_current_password: 'Das aktuelle Passwort ist falsch.',
      err_locked: 'Zu viele Fehlversuche. Das Konto ist vorübergehend gesperrt; bitte versuchen Sie es später erneut.',
      err_locked_until: 'Zu viele Fehlversuche. Das Konto ist bis {time} gesperrt.',
      err_password_change_required: 'Bevor Sie fortfahren, wählen Sie Ihr eigenes Passwort.',
      err_session_replaced: 'Ihr Konto wurde auf einem anderen Gerät angemeldet, daher wurde diese Sitzung beendet. Bitte melden Sie sich erneut an.',
      err_taken: 'Dieser Wert ist bereits vergeben.',
      err_taken_phone: 'Es gibt bereits ein Konto mit dieser Telefonnummer.',
      err_taken_username: 'Dieser Benutzername ist vergeben. Bitte wählen Sie einen anderen.',
      err_phone_taken:
        'Für diese Nummer gibt es bereits ein Konto. Melden Sie sich auf der Anmeldeseite mit Ihrer Telefonnummer an oder wenden Sie sich an Ihren Administrator.',
      err_invalid_locale: 'Diese Sprache wird nicht unterstützt.',

      err_too_large: 'Die Datei ist zu groß.',
      err_too_large_limit: 'Die Datei ist zu groß: Erlaubt sind höchstens {limit}.',
      err_unsupported_type: 'Dieser Dateityp kann hier nicht hochgeladen werden.',
      err_bad_multipart: 'Die Datei konnte nicht gelesen werden. Versuchen Sie es erneut.',
      err_not_multipart: 'Die Datei wurde nicht korrekt gesendet. Versuchen Sie es erneut.',
      err_file_in_use: 'Diese Datei wird noch in Lektionen oder Abgaben verwendet. Entfernen Sie sie zuerst dort.',

      err_empty_patch: 'Es wurde nichts geändert.',
      err_no_changes: 'Es wurde nichts geändert.',
      err_nothing_to_update: 'Es wurde nichts geändert.',
      err_stale_order: 'Die Reihenfolge ist veraltet – jemand anderes hat sie geändert. Laden Sie die Seite neu und versuchen Sie es erneut.',
      err_no_platform: 'Die Plattform konnte nicht ermittelt werden. Prüfen Sie die Adresse.',
      err_last_owner: 'Die Plattform braucht mindestens eine Inhaberin oder einen Inhaber. Ernennen Sie zuerst jemand anderen dazu.',
      err_stream_in_use: 'In diesem Stream sind noch Lernende. Verschieben Sie sie zuerst in einen anderen Stream.',
      err_group_in_use: 'In dieser Gruppe sind noch Lernende. Verschieben Sie sie zuerst in eine andere Gruppe.',

      err_no_homework: 'Diese Lektion hat keine Hausaufgabe.',
      err_homework_has_submissions: 'Zu dieser Hausaufgabe gibt es bereits Abgaben, daher kann sie nicht entfernt werden.',
      err_homework_required: 'Die Abschlussregeln der Lektion verlangen noch eine Hausaufgabe. Deaktivieren Sie diese Bedingung zuerst.',
      err_submission_pending: 'Ihre letzte Abgabe wartet noch auf die Bewertung.',

      err_blocked: 'Diese Lektion ist für Sie noch nicht freigeschaltet.',
      err_blocked_progress: 'Schließen Sie zuerst die vorherigen Lektionen ab.',
      err_blocked_homework: 'Reichen Sie zuerst die vorherige Hausaufgabe ein und warten Sie, bis sie angenommen wurde.',
      err_blocked_schedule: 'Dieses Modul wird später freigeschaltet.',
      err_blocked_access: 'Dieses Modul ist für Sie gesperrt.',
      err_blocked_unpublished: 'Diese Lektion ist noch nicht veröffentlicht.',
      err_bad_span: 'Die Fortschrittsmeldung war ungültig. Bitte laden Sie die Seite neu.',
      err_not_gateable: 'Dieser Block erfasst keinen Wiedergabefortschritt.',

      err_video_busy: 'Dieses Video wird gerade an anderer Stelle hochgeladen oder konvertiert. Warten Sie, bis das abgeschlossen ist.',
      err_video_has_media: 'Dieses Video hat bereits eine Aufnahme. Legen Sie für eine andere Aufnahme ein neues Video an.',
      err_video_has_no_media: 'Das Video kann nicht als bereit markiert werden: Seine Datei fehlt auf dem Server.',
      err_video_in_use: 'Dieses Video wird noch in Lektionen verwendet. Entfernen Sie es zuerst dort.',
      err_video_media_missing: 'Die Videodatei fehlt auf dem Server. Wenden Sie sich an die Administration.',
      err_video_misconfigured: 'Das Video ist falsch eingerichtet: Die ID beim Hosting-Dienst fehlt.',
      err_video_not_local: 'Dieses Video liegt bei einem externen Dienst; laden Sie die Datei dort hoch.',
      err_video_not_ready: 'Das Video ist noch nicht bereit.',
      err_video_transcoding: 'Das Video wird noch konvertiert. Warten Sie, bis das abgeschlossen ist.',
      err_ffprobe_missing: 'Der Server kann keine Videos prüfen, weil ffprobe nicht installiert ist. Wenden Sie sich an die Administration.',

      err_bad_filter: 'Ein Filterwert ist ungültig.',
      err_bad_action: 'Die Aktion im Filter ist ungültig.',
      err_bad_actor_id: 'Die Person im Filter ist ungültig.',
      err_bad_from: 'Das Startdatum ist ungültig.',
      err_bad_to: 'Das Enddatum ist ungültig.',
      err_bad_range: 'Das Startdatum darf nicht nach dem Enddatum liegen.',
      err_bad_grouping: 'Wählen Sie die Gruppierung nach Modulen oder nach Lektionen.',
      err_bad_report: 'Diesen Bericht gibt es nicht.',
      err_course_required: 'Wählen Sie zuerst einen Kurs aus.',
      err_missing_course: 'Wählen Sie zuerst einen Kurs aus.',
      err_bad_amount: 'Die Punktzahl ist ungültig.',
      err_bad_award: 'Wählen Sie eine lernende Person und einen Kurs aus, um Punkte zu vergeben.',
      err_bad_reason: 'Der Grund für die Punkte ist ungültig.',
      err_gamification_disabled: 'Für diesen Kurs sind Punkte deaktiviert.',

      field_required: 'Bitte füllen Sie „{field}“ aus.',
      field_too_long: '„{field}“ ist zu lang: höchstens {max} Zeichen.',
      field_length: '„{field}“ muss {min} bis {max} Zeichen lang sein.',
      field_range: '„{field}“ muss zwischen {min} und {max} liegen.',
      field_bad_phone: 'Die Telefonnummer ist ungültig. Geben Sie sie mit Ländervorwahl ein, z. B. +998 90 123 45 67.',
      field_color: 'Geben Sie die Farbe als #f60 oder #ff6600 ein.',
      field_bad_url: 'Der Link ist ungültig: Geben Sie eine vollständige Adresse ein, die mit https:// beginnt.',
      field_not_enrolled: 'Diese Person ist nicht in diesem Kurs eingeschrieben.',
      field_same_password: 'Das ist bereits Ihr Passwort. Bitte wählen Sie ein anderes.',
      field_username_shape: 'Ein Benutzername hat {min}–{max} Zeichen: Buchstaben, Ziffern, Punkt, Bindestrich oder Unterstrich.',
      field_invalid: 'Das Feld „{field}“ ist nicht korrekt ausgefüllt.',

      field_title: 'Titel',
      field_name: 'Name',
      field_description: 'Beschreibung',
      field_full_name: 'Vollständiger Name',
      field_phone: 'Telefonnummer',
      field_phones: 'Telefonnummern',
      field_username: 'Benutzername',
      field_login: 'Benutzername oder Telefon',
      field_password: 'Passwort',
      field_current_password: 'Aktuelles Passwort',
      field_new_password: 'Neues Passwort',
      field_email: 'E-Mail',
      field_locale: 'Sprache',
      field_kind: 'Typ',
      field_status: 'Status',
      field_grade: 'Note',
      field_body: 'Antworttext',
      field_instructions: 'Aufgabenstellung',
      field_questions: 'Fragen',
      field_pass_pct: 'Bestehensgrenze (%)',
      field_ids: 'Reihenfolge',
      field_access: 'Zugang',
      field_access_start_at: 'Freischaltdatum',
      field_file: 'Datei',
      field_files: 'Dateien',
      field_section_id: 'Modul',
      field_course_id: 'Kurs',
      field_user_id: 'Lernende Person',
      field_students: 'Lernende',
      field_stream_id: 'Stream',
      field_group_id: 'Gruppe',
      field_mentor_id: 'Mentor',
      field_note: 'Notiz',
      field_comment: 'Kommentar',
      field_review_comment: 'Kommentar',
      field_role: 'Rolle',
      field_reason: 'Grund',
      field_reason_text: 'Begründung',
      field_amount: 'Punktzahl',
      field_points: 'Punkte',
      field_min_video_watch_percent: 'Erforderlicher Anteil (%)',
      field_provider: 'Videodienst',
      field_duration_seconds: 'Dauer',
      field_source_path: 'Dateipfad',
      field_external_id: 'Externe ID',
      field_payload: 'Blockinhalt',
      field_video_id: 'Video',
      field_file_id: 'Datei',
      field_url: 'Link',
      field_height: 'Höhe',
      field_html: 'Text',
      field_colors: 'Farbe',
      field_logo_path: 'Logo',
      field_favicon_path: 'Favicon',
      field_subdomain: 'Subdomain',
      field_cover_file_id: 'Titelbild',
      field_image_file_id: 'Modulbild',
    },
  },
)
